import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useWebhookConfigs, useToggleWebhook, type WebhookConfig } from '@/hooks/useWebhookConfigs';

interface WebhookListProps {
  onConfigClick: (config: WebhookConfig) => void;
}

export function WebhookList({ onConfigClick }: WebhookListProps) {
  const { data: configs, isLoading } = useWebhookConfigs();
  const toggleMutation = useToggleWebhook();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  if (!configs?.length) {
    return <p className="text-muted-foreground text-center py-8">Nenhum webhook configurado.</p>;
  }

  return (
    <div className="space-y-2">
      {configs.map((config) => (
        <div
          key={config.id}
          className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Switch
              checked={config.is_active}
              onCheckedChange={(checked) =>
                toggleMutation.mutate({ id: config.id, is_active: checked })
              }
              disabled={toggleMutation.isPending}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground truncate">
                  {config.display_name}
                </span>
                <Badge variant={config.is_active ? 'default' : 'secondary'} className="text-xs">
                  {config.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
              {config.description && (
                <p className="text-sm text-muted-foreground truncate">{config.description}</p>
              )}
              <p className="text-xs text-muted-foreground font-mono">{config.webhook_name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onConfigClick(config)}
            title="Configurar"
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
